<script>
  import { getContext, tick } from "svelte";
  import { distSq } from "vector";
  import { bucketArrayMultiple } from "overline";
  import { Modal, draggable } from "svelte-utilities";
  import range from "lodash/range";
  import cloneDeep from "lodash/cloneDeep";
  import isEmpty from "lodash/isEmpty";
  import isEqual from "lodash/isEqual";

  import ReportTable from "#lib/home/ReportTable.svelte";
  import ReportDownloadModal from "#lib/home/ReportDownloadModal.svelte";
  import GroupSummary from "#lib/home/GroupSummary.svelte";
  import GearIcon from "@local/assets/icons/gear.svg";
  import PlusIcon from "@local/assets/icons/plus.svg";
  import TrashIcon from "@local/assets/icons/trash.svg";
  import DownloadIcon from "@local/assets/icons/download.svg";
  import ReportSettings from "#lib/settings/ReportSettings.svelte";
  import { createReport, prepareReportData } from "@local/lamina-core";
  import { formatColumns } from "#src/extensions/format-columns.js";
  import { orderedList } from "@local/extensions/collections/sortable-list.js";
  import { discontiguousKey } from "#src/extensions/event-keys.js";
  import makeMultiReport from "#src/extensions/multi-report.js";
  import { incrementName } from "@local/extensions/identifiers/name.js";
  import { reviewing } from "#src/stores/ui.js";
  import eb from "#src/extensions/event-bus.js";

  export let group;
  export let items;
  export let collections;
  export let types;
  export let documentNotes;
  export let disabled;
  export let customColumns;
  export let customColColumns;

  const org = getContext("org");
  const reportTab = getContext("reportTab");
  const selectedReports = getContext("selectedReports");

  function transform(coll, xf, defaultValue = []) {
    return coll ? xf(coll) : defaultValue;
  }

  let newReport = null;
  let editedReport = null;
  let newReportModal;
  let reportModal;
  let confirmRemoveReportModal;
  let reportToDelete = null;
  let dragging = null;
  let dragStartPt = null;
  let isDragging = false;

  $: itemReports = $group.item_reports.order.map((i) => $group.item_reports[i]);
  $: checkTab(itemReports);
  $: allAttachments = $group && bucketArrayMultiple($group.attachments, "item_id", "type_id");
  $: currentReport = itemReports[$reportTab];
  $: orgColumns = transform($org?.data?.custom_columns, orderedList);
  $: jobColumns = transform($group?.data?.custom_columns, orderedList);
  $: standardColumns = $org?.data?.standard_columns || {};
  $: columns = formatColumns($group, types, orgColumns, jobColumns, standardColumns);
  $: reportData = prepareReportData(items, collections, types, documentNotes, $group, currentReport, columns);
  $: allCols = [
    ...reportData.columns,
    ...(reportData.totals.has_edge_treatment ? [{ label: "Edge Treatment", prop: "edge_treatment" }] : []),
    ...(reportData.totals.has_custom_shape ? [{ label: "Shape", prop: "is_custom_shape" }] : []),
  ];

  function checkTab(reports) {
    if (!reports[$reportTab]) {
      $reportTab = 0;
      if (reports[0]) {
        $selectedReports = { [reports[0].id]: true };
      } else {
        $selectedReports = {};
      }
    }
  }

  function beginAddingReport() {
    const maxIndex = itemReports.reduce((max, r) => (r.index > max ? r.index : max), 0);
    newReport = createReport({ group_id: $group.id, index: maxIndex + 1 });
    newReportModal.open();
  }

  function shiftSelectReport(index) {
    const min = Math.min($reportTab, index);
    const max = Math.max($reportTab, index);

    const selected = range(min, max + 1);
    const selectedIds = selected.map((idx) => $group.item_reports.order[idx]).filter((id) => id);
    $selectedReports = selectedIds.reduce((acc, id) => {
      acc[id] = true;
      return acc;
    }, {});
  }

  function discSelectReport(index) {
    const id = $group.item_reports.order[index];
    if (!$selectedReports[id]) {
      $selectedReports[id] = true;
    } else {
      delete $selectedReports[id];
    }
    $selectedReports = $selectedReports;
  }

  function selectReport(evt, index) {
    if (evt.shiftKey) {
      shiftSelectReport(index);
    } else if (discontiguousKey(evt)) {
      discSelectReport(index);
    } else {
      const id = $group.item_reports.order[index];
      $selectedReports = { [id]: true };
    }

    $reportTab = index;
  }

  function confirmRemoveReport(id) {
    reportToDelete = $group.item_reports[id];
    confirmRemoveReportModal.open();
  }

  function removeReport(id) {
    group.update([{ type: "item_report", action: "remove", ids: [id] }]);
  }

  function confirmUpdateReportSettings(evt, reportId) {
    newReport = null;

    const reportIndex = $group.item_reports.order.findIndex((id) => id === reportId);
    const report = $group.item_reports[reportId];

    if (evt.shiftKey || discontiguousKey(evt)) {
      $selectedReports[reportId] = true;
    } else if (!$selectedReports[reportId]) {
      $selectedReports = { [reportId]: true };
    }

    const reportIndices = $group.item_reports.order.reduce((acc, r, i) => {
      acc[r] = i;
      return acc;
    }, {});
    const selected = Object.keys($selectedReports).filter((id) => reportIndices[id] != undefined);
    const reports = selected.map((id) => cloneDeep($group.item_reports[id]));
    const multiReport = makeMultiReport(reports);

    if (report) {
      if (selected.length > 1) {
        newReport = {
          ...cloneDeep(multiReport),
          id: selected,
        };
        editedReport = cloneDeep(multiReport);
      } else {
        newReport = cloneDeep(report);
      }
      $reportTab = reportIndex;
    }

    if (newReport) {
      reportModal.open();
    }
  }

  function getDiff(a, b) {
    const diff = {};
    Object.keys(b).forEach((key) => {
      const aVal = a[key];
      const bVal = b[key];

      if (key === "hidden_columns") {
        const d = getDiff(aVal, bVal);
        diff[key] = d;
      } else {
        if (!isEqual(aVal, bVal)) {
          diff[key] = bVal;
        }
      }
    });
    return diff;
  }

  function updateReportSettings() {
    if (Array.isArray(newReport.id)) {
      const diff = getDiff(editedReport, newReport);
      const { id, hidden_columns, ...rest } = diff;
      const updates = id.map((reportId) => {
        const report = cloneDeep($group.item_reports[reportId]);
        if (!isEmpty(hidden_columns)) {
          const rhc = report.hidden_columns || {};
          Object.keys(hidden_columns).forEach((col) => {
            rhc[col] = hidden_columns[col];
          });
          return { type: "item_report", id: reportId, diff: { ...rest, hidden_columns: rhc } };
        } else {
          return { type: "item_report", id: reportId, diff: rest };
        }
      });
      group.update(updates);
    } else {
      const { id, group_id, ...rest } = newReport;
      group.update([{ type: "item_report", id, diff: rest }]);
    }
  }

  function updateReportSetting(e) {
    const { prop, value, diff } = e.detail;
    if (diff) {
      Object.keys(diff).forEach((k) => {
        newReport[k] = diff[k];
      });
    } else {
      newReport[prop] = value;
    }
  }

  function updateReportHidden(e) {
    const { prop, value } = e.detail;
    newReport.hidden_columns[prop] = value;
  }

  function cloneReport(e) {
    const { settings } = e.detail;
    const maxIndex = itemReports.reduce((max, r) => (r.index > max ? r.index : max), 0);
    const report = {
      ...cloneDeep(settings),
      index: maxIndex + 1,
      id: crypto.randomUUID(),
    };

    const sibs = itemReports.map((r) => r.report_name);
    report.report_name = incrementName(settings.report_name, sibs);
    group.update([{ type: "item_report", action: "add", records: [report] }]);

    if (reportModal) {
      reportModal.close();
    }
  }

  function addReport() {
    group.update([{ type: "item_report", action: "add", records: [newReport] }]);
  }

  function enterReviewMode(report) {
    $reviewing = {
      report,
      notes: reportData.sections[0].rows,
    };
  }

  function dragstart(e, id, index) {
    if (disabled) return;
    const { x, y } = e;
    dragStartPt = { x, y };
    dragging = index;
  }

  function drag(e, id, index) {
    if (dragging == null) return;
    const d = dragStartPt ? distSq(e, dragStartPt) : 0;
    if (d > 100) {
      isDragging = true;
    } else {
      isDragging = false;
    }
  }

  function dragend() {
    dragging = null;
    dragStartPt = null;
    isDragging = false;
  }

  async function drop(dropIndex) {
    if (!isDragging) return;
    const id = $group.item_reports.order[dragging];
    if (dragging === dropIndex || dropIndex === dragging - 1) return;
    const insertion = dropIndex < dragging ? dropIndex + 1 : dropIndex;
    const order = cloneDeep($group.item_reports.order);
    const moved = order.splice(dragging, 1)[0];
    order.splice(insertion, 0, moved);
    group.updateReportOrder(order);
    await tick();
    $selectedReports = { [id]: true };
    const index = $group.item_reports.indices[id];
    $reportTab = index;
  }
</script>

<div class="mx-4 text-sm font-bold flex gap-2 items-center flex-wrap border-b border-gray-300">
  {#each itemReports as report, i (report.id)}
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div
      use:draggable
      on:dragstart={(e) => dragstart(e.detail, report.id, i)}
      on:drag={(e) => drag(e.detail, report.id, i)}
      on:dragend={dragend}
      class="flex items-center gap-2 item-list-tab"
      class:dragging={isDragging && dragging === i}
      class:selected={$reportTab === i}
      class:highlighted={$selectedReports[report.id]}>
      {#if i === 0}
        <div class="drop-target left-target" class:visible={isDragging} on:mouseup={() => drop(-1)}>
          <div class="drop-target-line"></div>
        </div>
      {/if}
      <button on:click={(e) => selectReport(e, i)} class="p-2">
        {report.report_name || "Report"}
      </button>
      <div class="drop-target" class:visible={isDragging} on:mouseup={() => drop(i)}>
        <div class="drop-target-line" />
      </div>
    </div>
  {/each}
  {#if !disabled}
    <button on:click={beginAddingReport} class="mx-2 p-1 rounded hover:bg-gray-100">
      <PlusIcon />
    </button>
  {/if}
</div>
{#if currentReport}
  {#key currentReport.id}
    <div class="px-4 flex justify-between items-center">
      {#if currentReport.group_by === "notes"}
        <button class="btn btn-primary" on:click={() => enterReviewMode(currentReport)}
          >Enter Review Mode</button>
      {:else if currentReport.group_by === "linear"}
        <div class="grow flex gap-2 px-2">
          <div>
            <div class="font-bold text-base">
              {reportData.totals.collection_count}
            </div>
            <div class="text-xs text-gray-400 truncate">Openings</div>
          </div>
        </div>
      {:else}
        <GroupSummary {group} {items} {collections} {types} report={currentReport} data={reportData} />
      {/if}
      <div class="flex items-center gap-2">
        <button
          class="btn btn-primary flex items-center gap-1"
          on:click={() => eb.dispatch("open-report-download")}>
          <div>
            <DownloadIcon />
          </div>
          <div>Download</div>
        </button>
        {#if !disabled}
          <button class="btn btn-icon" on:click={(e) => confirmUpdateReportSettings(e, currentReport?.id)}>
            <GearIcon />
          </button>
          {#if itemReports.length > 1}
            <button class="btn btn-icon" on:click={() => confirmRemoveReport(currentReport.id)}>
              <TrashIcon />
            </button>
          {/if}
        {/if}
      </div>
    </div>
    <div class="pt-4 w-full relative">
      <ReportTable
        sent={false}
        data={reportData}
        columns={allCols}
        {group}
        report={currentReport}
        attachments={allAttachments}
        isReviewer />
    </div>
  {/key}
{:else if !itemReports.length}
  <div class="px-4 italic text-gray-500">No reports have been created.</div>
{/if}

<Modal
  bind:this={newReportModal}
  closeable
  width="36rem"
  fullframe
  buttons={[
    { label: "Cancel", type: "cancel" },
    { label: "Add Report", type: "confirm", style: "primary" },
  ]}
  on:confirm={addReport}>
  <div slot="title">Add New Report</div>
  <div slot="content" class="overflow-y-auto p-4">
    <div class="text-xs">
      <ReportSettings
        {group}
        {items}
        {documentNotes}
        {collections}
        settings={newReport}
        {standardColumns}
        {customColumns}
        {customColColumns}
        on:update-setting={updateReportSetting}
        on:toggle-column={updateReportHidden} />
    </div>
  </div>
</Modal>

<Modal
  bind:this={reportModal}
  closeable
  width="36rem"
  fullframe
  buttons={[
    { label: "Cancel", type: "cancel" },
    { label: "Update", type: "confirm", style: "primary" },
  ]}
  on:confirm={updateReportSettings}>
  <div slot="title">Update Report</div>
  <div slot="content" class="overflow-y-auto p-4">
    {#if Array.isArray(newReport.id) && newReport.id.length > 1}
      <div class="font-bold italic text-xs">Editing {newReport.id.length} Reports</div>
    {/if}
    <div class="text-xs">
      <ReportSettings
        {group}
        {items}
        {collections}
        {documentNotes}
        settings={newReport}
        {standardColumns}
        {customColumns}
        {customColColumns}
        cloneable
        on:update-setting={updateReportSetting}
        on:clone-report={cloneReport}
        on:toggle-column={updateReportHidden} />
    </div>
  </div>
</Modal>

<Modal
  bind:this={confirmRemoveReportModal}
  closeable
  buttons={[
    { label: "Cancel", type: "cancel" },
    { label: "Delete", type: "confirm", style: "danger" },
  ]}
  on:confirm={() => removeReport(reportToDelete?.id)}>
  <div slot="title">Delete Report</div>
  <div slot="content">
    <p>Are you sure you want to delete this report?</p>
    <p class="pl-4 truncate">{reportToDelete?.report_name}</p>
  </div>
</Modal>

<ReportDownloadModal {group} {types} name={$group.job.name} reportId={currentReport?.id} />

<style lang="scss">
  .item-list-tab {
    @apply relative border-transparent border text-gray-400;

    &.dragging {
      @apply border opacity-30 bg-gray-200 border-t-gray-400 border-r-gray-400 border-l-gray-400;
    }

    &.selected {
      @apply border-b-black text-black;
    }

    &.highlighted {
      @apply text-black;
    }
  }

  .drop-target {
    @apply absolute w-full h-full z-30 pointer-events-none opacity-50;
    right: calc(-50% - 0.3rem);
    top: 0;

    &.left-target {
      left: calc(-50% - 0.3rem);
    }

    &.visible {
      pointer-events: auto;

      &:hover {
        .drop-target-line {
          @apply h-full border-l border-gray-400 absolute;
          left: 50%;
        }
      }
    }
  }
</style>
